import { useId, useState } from "react";
import { Box, Button, IconButton, Popover, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { tournamentTerm, type TournamentTermKey } from "@bw/shared/tournaments";

import { TournamentTermsSheet } from "./TournamentTermsSheet";

/** ⓘ next to a tournament word: the player wording in a popover, with
 * "All terms" opening the full sheet (#2691, F25). */
export function PlayerTermHelp({ term }: { term: TournamentTermKey }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const titleId = useId();
  const entry = tournamentTerm(term);
  return (
    <>
      <IconButton
        aria-label={`What is ${entry.label.toLowerCase()}?`}
        onClick={(event) => setAnchor(event.currentTarget)}
        size="small"
        sx={{
          p: 0.25,
          color: "text.secondary",
          // An invisible 44 px tap target around the small icon.
          position: "relative",
          "&::before": { content: '""', position: "absolute", inset: "-11px" },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Popover
        anchorEl={anchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={() => setAnchor(null)}
        open={Boolean(anchor)}
        slotProps={{ paper: { "aria-labelledby": titleId, role: "dialog", sx: { p: 1.5, maxWidth: 300 } } as object }}
      >
        <Typography id={titleId} sx={{ fontWeight: 700, fontSize: "0.9375rem" }}>
          {entry.label}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: "0.875rem", mt: 0.5 }}>
          {entry.player}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Button
            onClick={() => {
              setAnchor(null);
              setSheetOpen(true);
            }}
            size="small"
            sx={{ px: 0, minWidth: 0 }}
          >
            All terms
          </Button>
        </Box>
      </Popover>
      <TournamentTermsSheet focusTerm={term} onClose={() => setSheetOpen(false)} open={sheetOpen} />
    </>
  );
}
